import { FileInfo } from '@/api/DTO/common.interface';
import type {
	DOMConversionMap,
	DOMExportOutput,
	EditorConfig,
	LexicalEditor,
	LexicalNode,
	NodeKey,
	SerializedEditor,
	SerializedLexicalNode,
	Spread,
} from 'lexical';

import { $applyNodeReplacement, createEditor, DecoratorNode } from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';

const VideoComponent = React.lazy(() => import('./VideoComponent'));

export type Position = 'left' | 'right' | 'full' | undefined;

export interface VideoPayload {
	fileObject: FileInfo;
	thumbnailSrc: string;
	altText: string;
	caption?: LexicalEditor;
	height?: number;
	key?: NodeKey;
	showCaption?: boolean;
	src: string;
	width?: number;
	position?: Position;
}

export interface UpdateVideoPayload {
	altText?: string;
	showCaption?: boolean;
	position?: Position;
	fileObject?: FileInfo;
	thumbnailSrc?: string;
}

export type SerializedVideoNode = Spread<
	{
		fileObject: FileInfo;
		thumbnailSrc: string;
		altText: string;
		caption: SerializedEditor;
		height?: number;
		showCaption: boolean;
		src: string;
		width?: number;
		position?: Position;
	},
	SerializedLexicalNode
>;

export class VideoNode extends DecoratorNode<JSX.Element> {
	__fileObject: FileInfo;
	__thumbnailSrc: string;
	__src: string;
	__altText: string;
	__width: 'inherit' | number;
	__height: 'inherit' | number;
	__showCaption: boolean;
	__caption: LexicalEditor;
	__position: Position;

	static getType(): string {
		return 'video';
	}

	static clone(node: VideoNode): VideoNode {
		return new VideoNode(
			node.__fileObject,
			node.__thumbnailSrc,
			node.__src,
			node.__altText,
			node.__position,
			node.__width,
			node.__height,
			node.__showCaption,
			node.__caption,
			node.__key
		);
	}

	static importJSON(serializedNode: SerializedVideoNode): VideoNode {
		const {
			fileObject,
			thumbnailSrc,
			altText,
			height,
			width,
			caption,
			src,
			showCaption,
			position,
		} = serializedNode;
		const node = $createVideoNode({
			fileObject,
			thumbnailSrc,
			altText,
			height,
			position,
			showCaption,
			src,
			width,
		});
		const nestedEditor = node.__caption;
		const editorState = nestedEditor.parseEditorState(caption.editorState);
		if (!editorState.isEmpty()) {
			nestedEditor.setEditorState(editorState);
		}
		return node;
	}

	static importDOM(): DOMConversionMap | null {
		return {
			img: () => null,
		};
	}

	constructor(
		fileObject: FileInfo,
		thumbnailSrc: string,
		src: string,
		altText: string,
		position: Position,
		width?: 'inherit' | number,
		height?: 'inherit' | number,
		showCaption?: boolean,
		caption?: LexicalEditor,
		key?: NodeKey
	) {
		super(key);
		this.__fileObject = fileObject;
		(this.__thumbnailSrc = thumbnailSrc), (this.__src = src);
		this.__altText = altText;
		this.__width = width || 'inherit';
		this.__height = height || 'inherit';
		this.__showCaption = showCaption || false;
		this.__caption = caption || createEditor();
		this.__position = position;
	}

	exportDOM(): DOMExportOutput {
		const element = document.createElement('img');
		element.setAttribute('src', this.__src);
		element.setAttribute('alt', this.__altText);
		element.setAttribute('width', this.__width.toString());
		return { element };
	}

	exportJSON(): SerializedVideoNode {
		return {
			fileObject: this.__fileObject,
			thumbnailSrc: this.__thumbnailSrc,
			altText: this.getAltText(),
			caption: this.__caption.toJSON(),
			height: this.__height === 'inherit' ? 0 : this.__height,
			position: this.__position,
			showCaption: this.__showCaption,
			src: this.getSrc(),
			type: 'video',
			version: 1,
			width: this.__width === 'inherit' ? 0 : this.__width,
		};
	}

	getSrc(): string {
		return this.__src;
	}

	getAltText(): string {
		return this.__altText;
	}

	setAltText(altText: string): void {
		const writable = this.getWritable();
		writable.__altText = altText;
	}

	setWidthAndHeight(
		width: 'inherit' | number,
		height: 'inherit' | number
	): void {
		const writable = this.getWritable();
		writable.__width = width;
		writable.__height = height;
	}

	getShowCaption(): boolean {
		return this.__showCaption;
	}

	setShowCaption(showCaption: boolean): void {
		const writable = this.getWritable();
		writable.__showCaption = showCaption;
	}

	getPosition(): Position {
		return this.__position;
	}

	setPosition(position: Position): void {
		const writable = this.getWritable();
		writable.__position = position;
	}

	update(payload: UpdateVideoPayload): void {
		const writable = this.getWritable();
		const { altText, showCaption, position, fileObject, thumbnailSrc } =
			payload;
		if (altText !== undefined) {
			writable.__altText = altText;
		}
		if (showCaption !== undefined) {
			writable.__showCaption = showCaption;
		}
		if (position !== undefined) {
			writable.__position = position;
		}
		if (fileObject !== undefined) {
			writable.__fileObject = fileObject;
		}
		if (thumbnailSrc !== undefined) {
			writable.__thumbnailSrc = thumbnailSrc;
		}
	}

	getFileObject(): FileInfo {
		return this.__fileObject;
	}

	setFileObject(fileObject: FileInfo): void {
		const writable = this.getWritable();
		writable.__src = fileObject.url;
		writable.__fileObject = fileObject;
	}

	getThumbnailSrc(): string {
		return this.__thumbnailSrc;
	}

	setThumbnailSrc(thumbnailSrc: string): void {
		const writable = this.getWritable();
		writable.__thumbnailSrc = thumbnailSrc;
	}

	// View

	createDOM(config: EditorConfig): HTMLElement {
		const span = document.createElement('span');
		const className = `${config.theme.videoThumbnail} position-${this.__position}`;
		if (className !== undefined) {
			span.className = className;
		}
		return span;
	}

	updateDOM(
		prevNode: VideoNode,
		dom: HTMLElement,
		config: EditorConfig
	): false {
		const position = this.__position;
		if (position !== prevNode.__position) {
			const className = `${config.theme.videoThumbnail} position-${position}`;
			if (className !== undefined) {
				dom.className = className;
			}
		}
		return false;
	}

	decorate(): JSX.Element {
		return (
			<Suspense fallback={null}>
				<VideoComponent
					src={this.__fileObject.url}
					thumbnailSrc={this.__thumbnailSrc}
					altText={this.__altText}
					width={this.__width}
					height={this.__height}
					nodeKey={this.getKey()}
					showCaption={this.__showCaption}
					caption={this.__caption}
					position={this.__position}
				/>
			</Suspense>
		);
	}
}

export function $createVideoNode({
	fileObject,
	thumbnailSrc,
	altText,
	position,
	height,
	src,
	width,
	showCaption,
	caption,
	key,
}: VideoPayload): VideoNode {
	return $applyNodeReplacement(
		new VideoNode(
			fileObject,
			thumbnailSrc,
			src,
			altText,
			position,
			width,
			height,
			showCaption,
			caption,
			key
		)
	);
}

export function $isVideoNode(
	node: LexicalNode | null | undefined
): node is VideoNode {
	return node instanceof VideoNode;
}
